/// <reference types="@types/google.maps" />
/* global google */
import type { TrackPoint, GoogleType, MapRefs } from './types.js'
import { SOURCE_NAMES } from './types.js'
import { accuracyColor, speedColor, gapColor, toSGTimeFull, formatDuration, computeDistance } from './helpers.js'

function clearMapRefs(refs: MapRefs) {
  refs.markers.forEach((m) => m.setMap(null))
  refs.polylines.forEach((p) => p.setMap(null))
  refs.circles.forEach((c) => c.setMap(null))
  refs.labels.forEach((l) => l.setMap(null))
  refs.listeners.forEach((l) => l.remove())
  refs.markers.length = 0
  refs.polylines.length = 0
  refs.circles.length = 0
  refs.labels.length = 0
  refs.listeners.length = 0
}

export type RenderResult = {
  statsText: string
}

export function renderMapPoints(
  g: GoogleType,
  map: google.maps.Map,
  infoWindow: google.maps.InfoWindow,
  refs: MapRefs,
  points: TrackPoint[],
  opts: {
    colorBySpeed: boolean
    showLabels: boolean
    showAccCircles: boolean
    fitBounds?: boolean
    isDarkMode?: boolean
  },
): RenderResult {
  clearMapRefs(refs)

  if (points.length === 0) return { statsText: 'No points for selected range' }

  const markerStroke = opts.isDarkMode ? '#0f172a' : '#fff'
  const labelColor = opts.isDarkMode ? '#e2e8f0' : '#333'
  const mutedText = opts.isDarkMode ? '#94a3b8' : '#888'
  const dividerColor = opts.isDarkMode ? '#334155' : '#eee'

  // Lines: connect every visible point directly in timestamp order, without
  // source grouping. Large gaps are still styled differently but not broken.
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!
    const cur = points[i]!
    const gapSec = (cur.epochMs - prev.epochMs) / 1000
    const color = opts.colorBySpeed ? speedColor(cur.speed) : gapColor(gapSec)
    const dashed = gapSec >= 120 && gapSec < 600
    refs.polylines.push(
      new g.maps.Polyline({
        path: [
          { lat: prev.latitude, lng: prev.longitude },
          { lat: cur.latitude, lng: cur.longitude },
        ],
        geodesic: true,
        strokeColor: color,
        strokeOpacity: gapSec >= 600 ? 0.3 : 0.7,
        strokeWeight: gapSec >= 600 ? 1 : 3,
        map,
        icons: dashed
          ? [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '15px' }]
          : [],
      }),
    )
  }

  // Markers
  const bounds = new g.maps.LatLngBounds()
  points.forEach((p, i) => {
    bounds.extend({ lat: p.latitude, lng: p.longitude })
    const color = opts.colorBySpeed ? speedColor(p.speed) : accuracyColor(p.horizontalAccuracy)
    const isFirst = i === 0
    const isLast = i === points.length - 1

    const marker = new g.maps.Marker({
      position: { lat: p.latitude, lng: p.longitude },
      map,
      icon: {
        path: g.maps.SymbolPath.CIRCLE,
        scale: isFirst || isLast ? 8 : 3.5,
        fillColor: isFirst ? '#34C759' : isLast ? '#FF3B30' : color,
        fillOpacity: isFirst || isLast ? 0.85 : 0.7,
        strokeColor: isFirst || isLast ? markerStroke : color,
        strokeWeight: isFirst || isLast ? 2.5 : 0.5,
      },
      zIndex: isFirst || isLast ? 1000 : i,
      title: `#${i + 1} ${toSGTimeFull(p.timestamp)}`,
    })

    marker.addListener('click', () => {
      const speed = p.speed != null ? (p.speed * 3.6).toFixed(1) + ' km/h' : 'N/A'
      const acc = p.horizontalAccuracy != null ? p.horizontalAccuracy.toFixed(1) + 'm' : 'N/A'
      const alt = p.altitude != null ? p.altitude.toFixed(1) + 'm' : 'N/A'
      const prev = i > 0 ? points[i - 1] : null
      const gap = prev ? formatDuration((p.epochMs - prev.epochMs) / 1000) : '—'
      const dist = prev
        ? computeDistance(g, prev.latitude, prev.longitude, p.latitude, p.longitude).toFixed(1) + 'm'
        : '—'

      infoWindow.setContent(`
        <div style="font-size:13px; line-height:1.7; min-width:200px;">
          <div style="font-weight:700; font-size:14px; margin-bottom:4px;">
            Point #${i + 1} <span style="color:${mutedText}; font-weight:400;">(id: ${p.id})</span>
          </div>
          <b>Time:</b> ${toSGTimeFull(p.timestamp)}<br>
          <b>Accuracy:</b> <span style="color:${accuracyColor(p.horizontalAccuracy)}">${acc}</span><br>
          <b>Speed:</b> ${speed}<br>
          <b>Altitude:</b> ${alt}<br>
          <b>Course:</b> ${p.course != null ? p.course.toFixed(0) + '°' : 'N/A'}<br>
          <b>Source:</b> ${SOURCE_NAMES[p.source] || p.source}<br>
          <b>Segment:</b> ${p.segmentIndex}<br>
          <hr style="margin:4px 0; border:none; border-top:1px solid ${dividerColor};">
          <b>Gap from prev:</b> ${gap}<br>
          <b>Dist from prev:</b> ${dist}<br>
          <b>Lat/Lng:</b> ${p.latitude.toFixed(6)}, ${p.longitude.toFixed(6)}
        </div>
      `)
      infoWindow.open(map, marker)
    })

    refs.markers.push(marker)

    if (opts.showLabels) {
      refs.labels.push(
        new g.maps.Marker({
          position: { lat: p.latitude, lng: p.longitude },
          map,
          icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0 },
          label: { text: String(i + 1), fontSize: '9px', color: labelColor, fontWeight: '600' },
          zIndex: 2000 + i,
          clickable: false,
        }),
      )
    }
  })

  // Accuracy circles
  if (opts.showAccCircles) {
    points.forEach((p) => {
      if (p.horizontalAccuracy == null) return
      const color = accuracyColor(p.horizontalAccuracy)
      refs.circles.push(
        new g.maps.Circle({
          center: { lat: p.latitude, lng: p.longitude },
          radius: p.horizontalAccuracy,
          fillColor: color,
          fillOpacity: 0.1,
          strokeColor: color,
          strokeOpacity: 0.3,
          strokeWeight: 1,
          map,
          clickable: false,
        }),
      )
    })
  }

  // Hide regular point markers when zoomed out (zoom < 15) so they don't
  // clutter the view — only the polyline paths are visible at overview zoom.
  // First/last markers (start/end) are always visible.
  const MARKER_ZOOM_THRESHOLD = 15
  const updateMarkerVisibility = () => {
    const zoom = map.getZoom() ?? 0
    const visible = zoom >= MARKER_ZOOM_THRESHOLD
    // Skip first (index 0) and last marker — they're always shown
    for (let j = 1; j < refs.markers.length - 1; j++) {
      refs.markers[j]!.setVisible(visible)
    }
  }
  updateMarkerVisibility()
  refs.listeners.push(map.addListener('zoom_changed', updateMarkerVisibility))

  // Fit bounds only when explicitly requested (e.g. initial load)
  if (opts.fitBounds !== false) map.fitBounds(bounds)

  // Compute stats
  let totalDist = 0
  let maxGap = 0
  let gapCount2min = 0
  let gapCount10min = 0
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!
    const cur = points[i]!
    totalDist += computeDistance(g, prev.latitude, prev.longitude, cur.latitude, cur.longitude)
    const gap = (cur.epochMs - prev.epochMs) / 1000
    if (gap > maxGap) maxGap = gap
    if (gap >= 120) gapCount2min++
    if (gap >= 600) gapCount10min++
  }
  const accs = points.filter((p) => p.horizontalAccuracy != null).map((p) => p.horizontalAccuracy!)
  const avgAcc = accs.length ? (accs.reduce((a, b) => a + b, 0) / accs.length).toFixed(1) : 'N/A'
  const sorted = [...accs].sort((a, b) => a - b)
  const medAcc = sorted.length ? sorted[Math.floor(sorted.length / 2)]!.toFixed(1) : 'N/A'
  const goodPct = accs.length ? ((accs.filter((a) => a < 30).length / accs.length) * 100).toFixed(0) : 'N/A'

  const first = points[0]!
  const last = points[points.length - 1]!
  const statsText =
    `${points.length} points | ${toSGTimeFull(first.timestamp)} → ${toSGTimeFull(last.timestamp)} | ` +
    `${(totalDist / 1000).toFixed(2)} km | Max gap: ${formatDuration(maxGap)} | ` +
    `Acc avg: ${avgAcc}m, med: ${medAcc}m, <30m: ${goodPct}% | ` +
    `Gaps: ${gapCount2min} >2min, ${gapCount10min} >10min`

  return { statsText }
}
